import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    toasts: Toast[];
    addToast: (message: string, type?: ToastType) => void;
    removeToast: (id: string) => void;
    success: (message: string) => void;
    error: (message: string) => void;
    warning: (message: string) => void;
    info: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = useCallback((message: string, type: ToastType = 'info') => {
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setToasts(prev => [...prev, { id, message, type }]);

        // Auto-remove after 5 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const success = useCallback((message: string) => addToast(message, 'success'), [addToast]);
    const error = useCallback((message: string) => addToast(message, 'error'), [addToast]);
    const warning = useCallback((message: string) => addToast(message, 'warning'), [addToast]);
    const info = useCallback((message: string) => addToast(message, 'info'), [addToast]);

    return (
        <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

// Toast Container Component — bottom-center paper pills. Status is a
// small muted dot, never a coloured field; surfaces/ink come from the
// theme tokens so dark mode needs no JS. The container stays mounted
// (pointer-events: none when empty) so AnimatePresence can play exits.
const ToastContainer: React.FC<{ toasts: Toast[]; removeToast: (id: string) => void }> = ({
                                                                                              toasts,
                                                                                              removeToast
                                                                                          }) => {
    const reduceMotion = useReducedMotion();

    return (
        <div className="toast-stack" aria-live="polite">
            {/* No `layout` / `mode="popLayout"` here: combining framer's layout
                projection with the y-transform entrance makes the pill paint at a
                jumped position for one frame before the spring settles — read as a
                brief "flash/fold-in" at the bottom of the screen on every toast.
                Enter/exit is owned purely by the initial/animate/exit transforms;
                the column-reverse flex re-stacks any siblings on dismiss. */}
            <AnimatePresence initial={false}>
                {toasts.map(toast => (
                    <motion.div
                        key={toast.id}
                        className="toast-pill"
                        role="status"
                        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 18, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.96 }}
                        transition={
                            reduceMotion
                                ? { duration: 0.1 }
                                : { type: 'spring', stiffness: 520, damping: 34 }
                        }
                    >
                        <span className={`toast-pill__dot toast-pill__dot--${toast.type}`} aria-hidden="true" />
                        <span className="toast-pill__msg">{toast.message}</span>
                        <button
                            type="button"
                            className="toast-pill__close"
                            aria-label="Dismiss notification"
                            onClick={() => removeToast(toast.id)}
                        >
                            ×
                        </button>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};
